import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiUpload } from './client';
import type { Donation, FulfillAskRequest, PaginatedResponse, DonationStatus, DonationAuditEntry } from '@spi/shared';

export function useDonations(filters: { status?: DonationStatus; page?: number; limit?: number } = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));

  return useQuery({
    queryKey: ['donations', filters],
    queryFn: () => apiFetch<PaginatedResponse<Donation>>(`/api/transactions?${params}`),
  });
}

export function useDonation(id: number) {
  return useQuery({
    queryKey: ['donations', id],
    queryFn: () => apiFetch<Donation>(`/api/transactions/${id}`),
    enabled: id > 0,
  });
}

export function useFulfillAsk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: FulfillAskRequest) =>
      apiFetch<Donation>('/api/transactions', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['donations'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useShipDonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tracking }: { id: number; tracking?: string }) =>
      apiFetch<Donation>(`/api/transactions/${id}/ship`, {
        method: 'PUT',
        body: JSON.stringify({ shippingTracking: tracking }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['donations'] }),
  });
}

export function useDeliverDonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, shippingTracking, notes }: { id: number; shippingTracking?: string; notes?: string }) =>
      apiFetch<Donation>(`/api/transactions/${id}/deliver`, {
        method: 'PUT',
        body: JSON.stringify({ shippingTracking, notes }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['donations'] }),
  });
}

export function useReceiveDonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes, files }: { id: number; notes?: string; files?: File[] }) => {
      const formData = new FormData();
      if (notes) formData.append('notes', notes);
      if (files) files.forEach((f) => formData.append('files', f));
      return apiUpload<Donation>(`/api/transactions/${id}/receive`, formData, 'PUT');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['donations'] });
      qc.invalidateQueries({ queryKey: ['messages'] });
    },
  });
}

export function useVerifyDonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<Donation>(`/api/transactions/${id}/verify`, { method: 'PUT' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['donations'] }),
  });
}

export function useUpdateTracking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tracking }: { id: number; tracking: string }) =>
      apiFetch<Donation>(`/api/transactions/${id}/tracking`, {
        method: 'PUT',
        body: JSON.stringify({ shippingTracking: tracking }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['donations'] }),
  });
}

export function useAuditLog(id: number) {
  return useQuery({
    queryKey: ['donations', id, 'audit'],
    queryFn: () => apiFetch<DonationAuditEntry[]>(`/api/transactions/${id}/audit`),
    enabled: id > 0,
  });
}

export function useCancelDonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<Donation>(`/api/transactions/${id}/cancel`, { method: 'PUT' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['donations'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}
